import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import jsPDF from "jspdf";
import { api } from "../lib/apiClient";
import { Header, Footer } from "../components/Layout";
import { EvidenceBadge, ProgressBar } from "../components/Widgets";
import { Countdown } from "../components/Countdown";
import { LegifranceLink, ActionButtons } from "../components/JurisWidgets";
import { TTSButton } from "../components/TTSButton";
import { FileText, Mail, Edit3, Timer, ArrowLeft, ShieldAlert, Calendar, AlertCircle, Bell, CheckCircle2, Clock as ClockIcon } from "lucide-react";

export default function CaseResult() {
  const { caseId } = useParams();
  const [c, setC] = useState(null);
  const [notes, setNotes] = useState("");
  const [aj, setAj] = useState({ revenue: "", household: 1, result: null });
  const [alertEmail, setAlertEmail] = useState("");
  const [alertMsg, setAlertMsg] = useState("");

  const subscribeAlert = async () => {
    if (!alertEmail) return;
    setAlertMsg("");
    try {
      const r = await api.post("/alerts/subscribe", { case_id: caseId, email: alertEmail, days_before: 3 });
      if (r.data.email_sent) {
        setAlertMsg({ ok: true, text: "✓ Alerte email J-3 activée. Email de confirmation envoyé." });
      } else {
        setAlertMsg({ ok: false, text: "Alerte enregistrée, mais l'email de confirmation n'a pas pu être envoyé. Vous pouvez réessayer." });
      }
    } catch (e) {
      setAlertMsg({ ok: false, text: "Erreur : " + (e.response?.data?.detail || "réessayez") });
    }
  };

  useEffect(() => {
    api.get(`/cases/${caseId}`).then((r) => { setC(r.data); setNotes(r.data.annotations || ""); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const saveNotes = async () => {
    await api.put(`/cases/${caseId}/annotate`, { annotations: notes });
    alert("Annotations sauvegardées");
  };

  const checkAj = async () => {
    const r = await api.post("/aide-juridictionnelle/check", {
      revenue_monthly: parseFloat(aj.revenue) || 0,
      household_size: parseInt(aj.household) || 1,
    });
    setAj({ ...aj, result: r.data });
  };

  const exportPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const a = c.analysis || {};
    let y = 40;
    doc.setFont("helvetica", "bold").setFontSize(18).text("JURISIA — Fiche Flash", 40, y); y += 24;
    doc.setFontSize(10).setFont("helvetica", "normal").text(`Case ID: ${c.case_id}`, 40, y); y += 14;
    doc.text(`Type: ${c.case_type} | Mode: ${c.mode}`, 40, y); y += 20;

    const writeSection = (title, lines) => {
      doc.setFont("helvetica", "bold").setFontSize(12).text(title, 40, y); y += 16;
      doc.setFont("helvetica", "normal").setFontSize(10);
      lines.forEach((l) => {
        const wrapped = doc.splitTextToSize(l, 520);
        wrapped.forEach((w) => { if (y > 780) { doc.addPage(); y = 40; } doc.text(w, 40, y); y += 12; });
      });
      y += 8;
    };

    if (c.mode === "pro") {
      writeSection("Synthèse chronologique", (a.synthese_chronologique || []).map((e) => `${e.date} — ${e.fait}`));
      writeSection("Qualification & textes", (a.qualification_textes || []).map((e) => `[${e.evidence}] ${e.article}: ${e.contenu}`));
      writeSection("Jurisprudences", (a.jurisprudences || []).map((e) => `[${e.evidence}] ${e.reference}: ${e.principe}`));
      writeSection("Points de vigilance", a.points_vigilance || []);
      writeSection("Pistes stratégiques", a.pistes_strategiques || []);
    } else {
      writeSection("Votre situation", [a.situation_resume || ""]);
      writeSection("Vos droits", (a.vos_droits || []).map((e) => `[${e.evidence}] ${e.titre} — ${e.explication} (${e.article})`));
      writeSection("Décisions de référence", (a.decisions_reference || []).map((e) => `[${e.evidence}] ${e.reference}: ${e.principe}`));
      writeSection("Délais légaux", (a.delais_legaux || []).map((e) => `${e.action}: ${e.delai_jours} jours (${e.urgence})`));
    }
    if (notes) writeSection("Annotations", [notes]);
    doc.setFontSize(8).setTextColor(120).text(`Loi du 31/12/1971 — Outil d'aide à la pré-instruction, ne constitue pas une consultation juridique.`, 40, 820);
    doc.save(`jurisia-${c.case_id}.pdf`);
  };

  if (!c) return <div className="min-h-screen flex items-center justify-center"><div className="pulse-dot"></div></div>;
  const a = c.analysis || {};

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#1E293B] mb-4"><ArrowLeft className="w-4 h-4" /> Retour</Link>

        {/* Meta strip */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <span className="j-label">Fiche Flash · {c.mode === "pro" ? "Vue Avocat" : "Vue Particulier"}</span>
          <EvidenceBadge level={a.evidence_global || "amber"} />
          <Link to={`/timeline/${c.case_id}`} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="case-timeline-btn">
            <ClockIcon className="w-3.5 h-3.5" /> Voir la chronologie
          </Link>
          <div className="flex items-center gap-2 text-sm text-slate-600 ml-auto">
            <Timer className="w-4 h-4 text-[#10B981]" /> +{c.time_saved_minutes} min économisées
          </div>
        </div>

        <h1 className="j-h1 font-heading mb-2" data-testid="case-title">
          Flash Droit
        </h1>
        <div className="text-slate-600 mb-6">Case {c.case_id}</div>

        {a.error && (
          <div className="j-card mb-6 border-red-200 bg-red-50" data-testid="analysis-error">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-red-500 mt-0.5" />
              <div>
                <div className="font-bold text-red-700">Source insuffisante — Validation professionnelle obligatoire</div>
                <div className="text-sm text-red-600 mt-1">{a.error}</div>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {c.mode === "pro" ? (
              <>
                <Section title="1. Synthèse chronologique">
                  {(a.synthese_chronologique || []).map((e, i) => (
                    <div key={i} className="flex gap-4 py-2 border-b border-slate-100 last:border-0">
                      <div className="j-label whitespace-nowrap pt-1">{e.date}</div>
                      <div className="text-slate-700 text-sm leading-relaxed">{e.fait}</div>
                    </div>
                  ))}
                  {(!a.synthese_chronologique || a.synthese_chronologique.length === 0) && <div className="text-sm text-slate-500">Aucun fait daté extrait.</div>}
                </Section>
                <Section title="2. Qualification & textes applicables">
                  {(a.qualification_textes || []).map((e, i) => (
                    <div key={i} className="py-3 border-b border-slate-100 last:border-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap"><span className="font-heading font-bold text-[#1E293B]">{e.article}</span><EvidenceBadge level={e.evidence} /><LegifranceLink url={e.legifrance_url} /></div>
                      <p className="text-sm text-slate-600 leading-relaxed">{e.contenu}</p>
                    </div>
                  ))}
                </Section>
                <Section title="Jurisprudences clés">
                  {(a.jurisprudences || []).map((e, i) => (
                    <div key={i} className="py-3 border-b border-slate-100 last:border-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap"><span className="font-mono text-sm text-[#10B981]">{e.reference}</span><EvidenceBadge level={e.evidence} /><LegifranceLink url={e.legifrance_url} label="Rechercher" /></div>
                      <p className="text-sm text-slate-600 leading-relaxed">{e.principe}</p>
                    </div>
                  ))}
                </Section>
                <Section title="3. Points de vigilance procédure">
                  <ul className="space-y-2">
                    {(a.points_vigilance || []).map((v, i) => (
                      <li key={i} className="flex gap-2 text-sm text-slate-700"><AlertCircle className="w-4 h-4 text-[#F59E0B] mt-0.5 flex-shrink-0" /> {v}</li>
                    ))}
                  </ul>
                </Section>
                <Section title="4. Pistes d'analyse à approfondir">
                  <ol className="space-y-2 list-decimal pl-4">
                    {(a.pistes_strategiques || []).map((v, i) => (
                      <li key={i} className="text-sm text-slate-700 leading-relaxed">{v}</li>
                    ))}
                  </ol>
                </Section>
              </>
            ) : (
              <>
                <Section title="Votre situation">
                  <p className="text-slate-700 leading-relaxed">{a.situation_resume}</p>
                </Section>
                <Section title="Vos droits & la loi">
                  {(a.vos_droits || []).map((d, i) => (
                    <div key={i} className="py-3 border-b border-slate-100 last:border-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap"><span className="font-heading font-bold text-[#1E293B]">{d.titre}</span><EvidenceBadge level={d.evidence} /><LegifranceLink url={d.legifrance_url} /></div>
                      <p className="text-sm text-slate-600 leading-relaxed">{d.explication}</p>
                      <div className="text-xs text-slate-400 font-mono mt-1">{d.article}</div>
                    </div>
                  ))}
                </Section>
                {a.decisions_reference && a.decisions_reference.length > 0 && (
                  <Section title="Décisions de justice de référence">
                    {a.decisions_reference.map((e, i) => (
                      <div key={i} className="py-2 border-b border-slate-100 last:border-0">
                        <div className="flex items-center gap-2 flex-wrap"><span className="font-mono text-sm text-[#10B981]">{e.reference}</span><EvidenceBadge level={e.evidence} /><LegifranceLink url={e.legifrance_url} label="Rechercher" /></div>
                        <p className="text-sm text-slate-600">{e.principe}</p>
                      </div>
                    ))}
                  </Section>
                )}
                {a.checklist_urgence && a.checklist_urgence.length > 0 && (
                  <Section title="Checklist d'action d'urgence">
                    <ol className="space-y-2 list-decimal pl-4" data-testid="checklist-urgence">
                      {a.checklist_urgence.map((v, i) => (
                        <li key={i} className="text-sm text-slate-700 leading-relaxed">{v}</li>
                      ))}
                    </ol>
                  </Section>
                )}
                <Section title="Radar d'urgence & délais">
                  {(a.delais_legaux || []).map((d, i) => (
                    <Countdown key={i} days={d.delai_jours || 0} urgence={d.urgence || "normale"} actionLabel={d.action} />
                  ))}
                  <div className="mt-4 p-3 rounded-lg border border-slate-200 bg-slate-50">
                    <div className="j-label mb-2 flex items-center gap-2"><Bell className="w-3 h-3" /> Alerte email J-3</div>
                    <div className="flex gap-2">
                      <input type="email" className="j-input flex-1" placeholder="votre@email.com" value={alertEmail} onChange={(e) => setAlertEmail(e.target.value)} data-testid="alert-email-input" />
                      <button onClick={subscribeAlert} className="btn-navy" data-testid="alert-subscribe-btn">Activer</button>
                    </div>
                    {alertMsg && (
                      <div className={`text-xs mt-2 font-semibold ${alertMsg.ok ? "text-feedback-success" : "text-feedback-error"}`} data-testid="alert-msg">
                        {alertMsg.text}
                      </div>
                    )}
                  </div>
                </Section>
                <Section title="Aide Juridictionnelle · Simulateur">
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div><label className="j-label mb-1 block">Revenu mensuel (€)</label><input className="j-input" value={aj.revenue} onChange={(e) => setAj({ ...aj, revenue: e.target.value })} data-testid="aj-revenue" /></div>
                    <div><label className="j-label mb-1 block">Foyer (personnes)</label><input className="j-input" type="number" min="1" value={aj.household} onChange={(e) => setAj({ ...aj, household: e.target.value })} data-testid="aj-household" /></div>
                  </div>
                  <button onClick={checkAj} className="btn-navy" data-testid="aj-check-btn">Vérifier éligibilité</button>
                  {aj.result && (
                    <div className={`mt-3 p-3 rounded-lg text-sm ${aj.result.eligible ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-slate-50 text-slate-700 border border-slate-200"}`} data-testid="aj-result">
                      {aj.result.message}
                    </div>
                  )}
                </Section>
              </>
            )}
          </div>

          {/* SIDEBAR */}
          <aside className="space-y-6">
            <div className="j-card">
              <ProgressBar value={a.dossier_ready_score || (a.error ? 0 : (a.evidence_global === 'green' ? 80 : a.evidence_global === 'amber' ? 55 : 30))} />
              {(a.pieces_manquantes && a.pieces_manquantes.length > 0) && (
                <div className="mt-4">
                  <div className="j-label mb-2">Pièces manquantes</div>
                  <ul className="text-sm text-slate-700 space-y-1">
                    {a.pieces_manquantes.map((p, i) => <li key={i}>• {p}</li>)}
                  </ul>
                </div>
              )}
            </div>

            <div className="j-card">
              <div className="j-label mb-3">Actions</div>
              <div className="space-y-2">
                <button onClick={exportPdf} className="btn-emerald w-full" data-testid="export-pdf-btn"><FileText className="w-4 h-4" /> Exporter PDF</button>
                <TTSButton getText={() => {
                  const parts = [];
                  if (a.situation_resume) parts.push(a.situation_resume);
                  (a.vos_droits || []).forEach(d => parts.push(`${d.titre}. ${d.explication}`));
                  (a.checklist_urgence || []).forEach((s, i) => parts.push(`Étape ${i+1}: ${s}`));
                  return parts.join(". ");
                }} label="Écouter le rapport" />
                <button onClick={() => window.location.href = `mailto:?subject=Fiche JURISIA ${c.case_id}&body=Voir dossier`} className="btn-ghost w-full" data-testid="email-btn"><Mail className="w-4 h-4" /> Envoyer au client</button>
                <button
                  onClick={() => {
                    const url = `${window.location.origin}/demo/${c.case_id}`;
                    navigator.clipboard.writeText(url);
                    alert("Lien public copié (données anonymisées) : " + url);
                  }}
                  className="btn-ghost w-full"
                  data-testid="share-demo-btn"
                >
                  🔗 Partager (lien public anonymisé)
                </button>
              </div>
            </div>

            <div className="j-card">
              <div className="j-label mb-3 flex items-center gap-2"><Edit3 className="w-3 h-3" /> Annotations</div>
              <textarea className="j-textarea" rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes personnelles..." data-testid="annotations-input" />
              <button onClick={saveNotes} className="btn-navy w-full mt-2" data-testid="save-notes-btn">Sauvegarder</button>
            </div>

            {c.mode === "citizen" && (
              <div className="j-card">
                <div className="j-label mb-3">Passer à l'action</div>
                <ActionButtons caseId={caseId} mode="citizen" />
                <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-500">
                  Dossier d'instruction certifié prêt à l'envoi (PDF) · Sources officielles Légifrance / Judilibre.
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="j-card">
      <h3 className="font-heading font-black text-xl text-[#1E293B] mb-3">{title}</h3>
      <div>{children}</div>
    </div>
  );
}
